import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { animate, createScope, createTimeline, stagger, type Scope } from "animejs";
import s from "../css/HomePage.module.css";

const appRoutes = [
  {
    label: "Calendar",
    path: "/calendar",
    initials: "CL",
    description: "Scheduled items",
    accent: "#a78bfa",
  },
  {
    label: "Quotes",
    path: "/quotes",
    initials: "QT",
    description: "Global Quotes from the Internet",
    accent: "#f472b6",
  },
  {
    label: "Pommodoro",
    path: "/anime",
    initials: "PM",
    description: "Pommodoro Timer",
    accent: "#65f4d9",
  },
  {
    label: "Tasks",
    path: "/tasks",
    initials: "TK",
    description: "Track work and priorities",
    accent: "#facc15",
  },
  {
    label: "Canvas",
    path: "/daily",
    initials: "CV",
    description: "Map daily notes with Canvas",
    accent: "#fb7185",
  }
] as const;

const backgroundCells = Array.from({ length: 48 }, (_, index) => index);
const signalNodes = Array.from({ length: 18 }, (_, index) => index);
let hasPlayedHomeOpening = false;

export function HomePage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const scopeRef = useRef<Scope | null>(null);
  const shouldPlayOpeningRef = useRef(!hasPlayedHomeOpening);
  const openingRef = useRef<HTMLDivElement>(null);
  const auraRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const flareRef = useRef<HTMLDivElement>(null);
  const [showOpening, setShowOpening] = useState(shouldPlayOpeningRef.current);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    scopeRef.current = createScope({ root: rootRef }).add(() => {
      const opening = openingRef.current;
      const aura = auraRef.current;
      const line = lineRef.current;
      const flare = flareRef.current;

      animate(`.${s.shell}`, {
        opacity: [0, 1],
        y: [20, 0],
        scale: [0.98, 1],
        duration: 900,
        ease: "outExpo",
      });

      animate(`.${s.routeTile}`, {
        opacity: [0, 1],
        y: [18, 0],
        delay: stagger(70),
        duration: 620,
        ease: "outCubic",
      });

      if (prefersReducedMotion) {
        hasPlayedHomeOpening = true;
        setShowOpening(false);
        return;
      }

      if (shouldPlayOpeningRef.current && opening && aura && line && flare) {
        createTimeline({
          defaults: { ease: "outExpo" },
          onComplete: () => {
            hasPlayedHomeOpening = true;
            setShowOpening(false);
          },
        })
          .add(line, {
            opacity: [0, 1],
            scaleY: [0.02, 1],
            scaleX: [0.5, 1],
            duration: 1200,
          })
          .add(
            aura,
            {
              opacity: [0, 0.9],
              scale: [0.3, 1],
              duration: 1500,
            },
            250,
          )
          .add(
            flare,
            {
              opacity: [0, 0.52, 0.26],
              scaleX: [0.25, 1.1],
              duration: 2100,
              ease: "inOutSine",
            },
            700,
          )
          .add(
            line,
            {
              scaleX: [1, 1.34, 0.92, 1.12],
              opacity: [1, 0.88, 1],
              duration: 1700,
              ease: "inOutSine",
            },
            1300,
          )
          .add(
            aura,
            {
              scale: [1, 1.32],
              opacity: [0.9, 0.58],
              duration: 1200,
              ease: "inOutSine",
            },
            2400,
          )
          .add(
            line,
            {
              scaleX: [1.12, 1.26],
              opacity: [1, 0.86],
              duration: 900,
              ease: "inOutSine",
            },
            3350,
          )
          .add(
            flare,
            {
              opacity: [0.26, 0.5, 0.12],
              scaleX: [1.1, 1.42],
              duration: 1200,
              ease: "inOutSine",
            },
            3600,
          )
          .add(
            opening,
            {
              opacity: [1, 0],
              duration: 1050,
              ease: "inOutSine",
            },
            4380,
          );
      }

      animate(`.${s.gridCell}`, {
        opacity: [0.08, 0.42],
        scale: [0.92, 1.04],
        delay: stagger(48, { grid: [8, 6], from: "center" }),
        duration: 1800,
        alternate: true,
        loop: true,
        ease: "inOutSine",
      });

      animate(`.${s.signalNode}`, {
        opacity: [0.22, 0.72],
        boxShadow: [
          "0 0 10px rgba(101, 244, 217, 0.32), 0 0 22px rgba(192, 132, 252, 0.18)",
          "0 0 24px rgba(101, 244, 217, 0.72), 0 0 52px rgba(192, 132, 252, 0.38)",
        ],
        delay: stagger(120, { from: "center" }),
        duration: 2400,
        alternate: true,
        loop: true,
        ease: "inOutSine",
      });

      animate(`.${s.scanline}`, {
        translateY: ["-20%", "120%"],
        opacity: [0, 0.34, 0],
        duration: 5200,
        loop: true,
        ease: "linear",
      });

      animate(`.${s.orbit}`, {
        rotate: "1turn",
        duration: 32000,
        loop: true,
        ease: "linear",
      });
    });

    return () => {
      scopeRef.current?.revert();
      scopeRef.current = null;
    };
  }, []);

  return (
    <main ref={rootRef} className={s.homeRoot}>
      <div className={s.background} aria-hidden="true">
        <div className={s.gridField}>
          {backgroundCells.map((cell) => (
            <span key={cell} className={s.gridCell} />
          ))}
        </div>
        <div className={s.orbit}>
          {signalNodes.map((node) => (
            <span
              key={node}
              className={s.signalNode}
              style={
                {
                  "--node-angle": `${node * 20}deg`,
                  "--node-distance": `${110 + (node % 6) * 34}px`,
                } as CSSProperties
              }
            />
          ))}
        </div>
        <div className={s.scanline} />
      </div>

      <section className={s.shell} aria-labelledby="home-title">
        <header className={s.header}>
          <div>
            <p className={s.eyebrow}>Dawn Diver</p>
          </div>
          <div className={s.statusPill}>Online</div>
        </header>

        <nav className={s.routeGrid} aria-label="Application routes">
          {appRoutes.map((route) => (
            <Link
              key={route.path}
              to={route.path}
              className={s.routeTile}
              style={{ "--route-accent": route.accent } as CSSProperties}
            >
              <span className={s.iconBox} aria-hidden="true">
                {route.initials}
              </span>
              <span className={s.routeText}>
                <span className={s.routeLabel}>{route.label}</span>
                <span className={s.routeDescription}>{route.description}</span>
              </span>
            </Link>
          ))}
        </nav>
      </section>

      {showOpening && (
        <div ref={openingRef} className={s.opening} aria-hidden="true">
          <div ref={auraRef} className={s.openingAura} />
          <div ref={flareRef} className={s.openingFlare} />
          <div ref={lineRef} className={s.openingLine} />
          <div className={s.openingDust} />
        </div>
      )}
    </main>
  );
}
